/**
 * Horse Detail Component
 *
 * Single horse listing detail page.
 * Mobile: touch swipe gallery, sticky CTA bar, inline message modal.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';

const SWIPE_THRESHOLD = 50;   // px minimum to register as swipe
const SWIPE_MAX_Y    = 80;    // px max vertical drift still counts as horizontal swipe

export default (initialData = {}) => ({
    mediaItems: [],
    activeIndex: 0,
    activeTab: 'desc',
    sendingMessage: false,
    listingId: initialData.listingId || null,
    isLoggedIn: Boolean(initialData.isLoggedIn),
    loginUrl: initialData.loginUrl || '',
    isFavorited: Boolean(initialData.isFavorited),
    togglingFav: false,

    // Mobile message modal
    showMessageModal: false,
    messageText: '',
    messageSent: false,

    // Listing report
    showReportModal: false,
    reportReason: 'fake',
    reportDetails: '',
    reporting: false,
    reportSent: false,
    reportError: '',

    // Fullscreen lightbox
    showLightbox: false,
    lightboxIndex: 0,
    _lightboxTouchStartX: 0,
    _lightboxTouchStartY: 0,

    // Sticky CTA bar visibility
    ctaHidden: false,   // true when sidebar CTA is visible in viewport

    // Touch tracking
    _touchStartX: 0,
    _touchStartY: 0,
    _intersectionObserver: null,
    _scrollHandler: null,

    // HorseFace láthatóság panel (csak tulajdonosnak)
    isOwner: false,
    isHorseface: false,
    hfVisibility: { passport: true, pedigree_number: true, documents: true },
    visibilityPanelOpen: false,
    visibilitySaving: false,
    visibilitySaveStatus: null, // null | 'success' | 'error'

    init() {
        const mediaFromPayload = Array.isArray(initialData.mediaItems) ? initialData.mediaItems : [];
        const imageFallback = Array.isArray(initialData.images)
            ? initialData.images.map((src) => ({ type: 'image', src }))
            : [];

        this.mediaItems = (mediaFromPayload.length ? mediaFromPayload : imageFallback).filter(
            (item) => item && typeof item.src === 'string' && item.src.length > 0
        );

        this.activeIndex = 0;

        // HorseFace visibility state inicializálás
        this.isOwner     = Boolean(initialData.isOwner);
        this.isHorseface = Boolean(initialData.isHorseface);
        if (initialData.hfVisibility && typeof initialData.hfVisibility === 'object') {
            this.hfVisibility = {
                passport:        Boolean(initialData.hfVisibility.passport !== false),
                pedigree_number: Boolean(initialData.hfVisibility.pedigree_number !== false),
                documents:       Boolean(initialData.hfVisibility.documents !== false),
            };
        }

        // Hide sticky CTA when the sidebar CTA (desktop) scrolls into view.
        // We watch the desktop contact card that only appears at lg+.
        this.$nextTick(() => {
            document.getElementById('lmk-horse-detail-preload')?.remove();

            const sidebarCta = document.getElementById('horse-detail-sidebar-cta');
            if (sidebarCta && typeof IntersectionObserver !== 'undefined') {
                this._intersectionObserver = new IntersectionObserver(
                    (entries) => {
                        this.ctaHidden = entries[0].isIntersecting;
                    },
                    { threshold: 0.4 }
                );
                this._intersectionObserver.observe(sidebarCta);
            }
        });
    },

    destroy() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
        }
    },

    get activeMedia() {
        return this.mediaItems[this.activeIndex] || null;
    },

    get hasMultipleMedia() {
        return this.mediaItems.length > 1;
    },

    setActiveImage(index) {
        if (index < 0 || index >= this.mediaItems.length) return;
        this.activeIndex = index;
    },

    nextImage() {
        if (!this.hasMultipleMedia) return;
        this.activeIndex = (this.activeIndex + 1) % this.mediaItems.length;
    },

    prevImage() {
        if (!this.hasMultipleMedia) return;
        this.activeIndex = (this.activeIndex - 1 + this.mediaItems.length) % this.mediaItems.length;
    },

    // Touch swipe handlers (attached via x-on:touchstart / x-on:touchend on the gallery)
    onTouchStart(e) {
        if (!this.hasMultipleMedia) return;
        const t = e.changedTouches[0];
        this._touchStartX = t.clientX;
        this._touchStartY = t.clientY;
    },

    onTouchEnd(e) {
        if (!this.hasMultipleMedia) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - this._touchStartX;
        const dy = Math.abs(t.clientY - this._touchStartY);

        // Only horizontal swipes (not vertical scrolls)
        if (dy > SWIPE_MAX_Y) return;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;

        if (dx < 0) {
            this.nextImage();
        } else {
            this.prevImage();
        }
    },

    switchTab(tab) {
        if (!tab) return;
        this.activeTab = tab;
    },

    onTabKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        const tabs = [...event.currentTarget.closest('[role="tablist"]').querySelectorAll('[role="tab"]')]
            .filter((tab) => tab.offsetParent !== null);
        const currentIndex = tabs.indexOf(event.currentTarget);
        if (currentIndex < 0) return;

        event.preventDefault();
        let nextIndex = event.key === 'Home' ? 0 : tabs.length - 1;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;

        tabs[nextIndex].click();
        tabs[nextIndex].focus();
    },

    // Lightbox
    openLightbox(index) {
        if (this.mediaItems[index] && this.mediaItems[index].type === 'image') {
            this.lightboxIndex = index;
            this.showLightbox = true;
            document.body.style.overflow = 'hidden';
        }
    },

    closeLightbox() {
        this.showLightbox = false;
        document.body.style.overflow = '';
    },

    lightboxNext() {
        // Skip video items
        let next = (this.lightboxIndex + 1) % this.mediaItems.length;
        let tries = 0;
        while (this.mediaItems[next] && this.mediaItems[next].type !== 'image' && tries < this.mediaItems.length) {
            next = (next + 1) % this.mediaItems.length;
            tries++;
        }
        this.lightboxIndex = next;
    },

    lightboxPrev() {
        let prev = (this.lightboxIndex - 1 + this.mediaItems.length) % this.mediaItems.length;
        let tries = 0;
        while (this.mediaItems[prev] && this.mediaItems[prev].type !== 'image' && tries < this.mediaItems.length) {
            prev = (prev - 1 + this.mediaItems.length) % this.mediaItems.length;
            tries++;
        }
        this.lightboxIndex = prev;
    },

    onLightboxTouchStart(e) {
        const t = e.changedTouches[0];
        this._lightboxTouchStartX = t.clientX;
        this._lightboxTouchStartY = t.clientY;
    },

    onLightboxTouchEnd(e) {
        const t = e.changedTouches[0];
        const dx = t.clientX - this._lightboxTouchStartX;
        const dy = Math.abs(t.clientY - this._lightboxTouchStartY);
        if (dy > SWIPE_MAX_Y) return;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        dx < 0 ? this.lightboxNext() : this.lightboxPrev();
    },

    // Mobile message modal
    openMessageModal() {
        if (!this.isLoggedIn) {
            window.location.href = this.loginUrl || ((window.lovasokParams && window.lovasokParams.homeUrl) || '') + '/bejelentkezes';
            return;
        }
        this.messageText = '';
        this.messageSent = false;
        this.showMessageModal = true;
        this.$nextTick(() => {
            const ta = document.getElementById('horse-detail-msg-textarea');
            if (ta) ta.focus();
        });
    },

    closeMessageModal() {
        this.showMessageModal = false;
    },

    async startConversation() {
        if (!this.isLoggedIn) {
            window.location.href = this.loginUrl || ((window.lovasokParams && window.lovasokParams.homeUrl) || '') + '/bejelentkezes';
            return;
        }
        if (!this.listingId) return;

        // On mobile the modal is used; on desktop we fall through to this.
        this.openMessageModal();
    },

    async sendModalMessage() {
        const text = (this.messageText || '').trim();
        if (!text || !this.listingId || this.sendingMessage) return;

        this.sendingMessage = true;
        try {
            const response = await LovasokAPI.post('messages', {
                listing_id: this.listingId,
                message: text
            });

            this.messageSent = true;

            if (response.conversation_id) {
                const homeUrl = (window.lovasokParams && window.lovasokParams.homeUrl) || '';
                setTimeout(() => {
                    window.location.href = `${homeUrl}/messages?conversation=${response.conversation_id}`;
                }, 800);
            }
        } catch (error) {
            console.error('Error starting conversation:', error);
            alert(error?.message || 'Hiba történt az üzenet küldése során.');
        } finally {
            this.sendingMessage = false;
        }
    },

    openReportModal() {
        if (!this.isLoggedIn) {
            window.location.href = this.loginUrl || ((window.lovasokParams && window.lovasokParams.homeUrl) || '') + '/bejelentkezes';
            return;
        }
        this.reportError = '';
        this.reportSent = false;
        this.showReportModal = true;
    },

    closeReportModal() {
        if (this.reporting) return;
        this.showReportModal = false;
    },

    async submitReport() {
        const details = (this.reportDetails || '').trim();
        if (!this.listingId || !details || this.reporting) return;

        this.reporting = true;
        this.reportError = '';
        try {
            await LovasokAPI.post(`horses/${this.listingId}/report`, {
                reason: this.reportReason,
                details,
            });
            this.reportSent = true;
            this.reportDetails = '';
        } catch (error) {
            this.reportError = error?.message || 'A bejelentést nem sikerült elküldeni.';
        } finally {
            this.reporting = false;
        }
    },

    async toggleFavorite() {
        if (!this.isLoggedIn) {
            const homeUrl = (window.lovasokParams && window.lovasokParams.homeUrl) || '';
            window.location.href = this.loginUrl || homeUrl + '/bejelentkezes';
            return;
        }
        if (this.togglingFav || !this.listingId) return;
        this.togglingFav = true;
        try {
            const data = await LovasokAPI.post(`horses/${this.listingId}/favorite`);
            this.isFavorited = data.favorited;
        } catch (e) {
            const msg = e?.message || '';
            if (msg.includes('401') || msg.includes('403') || msg.includes('jogosultság')) {
                window.location.href = this.loginUrl || ((window.lovasokParams?.homeUrl || '').replace(/\/$/, '') + '/bejelentkezes');
                return;
            }
            if (msg.includes('Saját hirdetés')) {
                alert('Saját hirdetésedet nem adhatod kedvencekhez.');
            }
            console.error('Favorite toggle failed:', e);
        } finally {
            this.togglingFav = false;
        }
    },

    // ── HorseFace láthatóság panel ────────────────────────────────────────

    openVisibilityPanel() {
        this.visibilitySaveStatus = null;
        this.visibilityPanelOpen  = true;
        document.body.style.overflow = 'hidden';
    },

    closeVisibilityPanel() {
        this.visibilityPanelOpen = false;
        document.body.style.overflow = '';
    },

    async saveHfVisibility() {
        if (!this.listingId || this.visibilitySaving) return;

        this.visibilitySaving    = true;
        this.visibilitySaveStatus = null;

        try {
            await LovasokAPI.post(`horses/${this.listingId}/hf-visibility`, {
                passport:        this.hfVisibility.passport,
                pedigree_number: this.hfVisibility.pedigree_number,
                documents:       this.hfVisibility.documents,
            });
            this.visibilitySaveStatus = 'success';
            // Auto-hide visszajelzés 3 mp után
            setTimeout(() => { this.visibilitySaveStatus = null; }, 3000);
        } catch (err) {
            console.error('HF visibility save error:', err);
            this.visibilitySaveStatus = 'error';
        } finally {
            this.visibilitySaving = false;
        }
    },
});
